import React, { useState, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { APIURL, TZ } from '../../../Global'
import './InvoiceModal.css'
import logo from '../../../assets/images/felixacare/ac_logo.png'

export default function InvoiceModal({ show, onHide, billId, hospitalId, invoiceData, onPaymentSuccess }) {
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'invoice' });
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [transactionRef, setTransactionRef] = useState('')
  const [remarks, setRemarks] = useState('')
  const [saving, setSaving] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({
    transactionRef: '',
    remarks: ''
  })
  const printRef = useRef()

  // Function to get currency symbol
  const getCurrencySymbol = (currency) => {
    if (!currency) return '$';

    const currencyLower = currency.toLowerCase();
    switch (currencyLower) {
      case 'crc':
      case 'col':
      case 'colon':
        return '₡';
      case 'usd':
      case 'dollar':
        return '$';
      case 'eur':
        return '€';
      case 'gbp':
        return '£';
      case 'inr':
        return '₹';
      default:
        return '$';
    }
  }

  // Function to format amount with currency
  const formatAmount = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${amount || 0}/-`;
  }

  const handlePrint = () => {
    window.print();
  }

  // Function to validate field value
  const validateField = (value, fieldName) => {
    if (fieldName === 'transactionRef') {
      if (!value.trim()) {
        return t('alerts.enterTransactionRef');
      }
      if (value !== value.trimStart()) {
        return t('alerts.leadingSpacesNotAllowed');
      }
    }
    return '';
  }

  // Function to prevent spaces in empty field
  const preventSpacesInEmptyField = (value, currentValue) => {
    // If field is empty and user tries to enter only spaces, prevent it
    if (!currentValue && value === ' ') {
      return currentValue; // Return current value (empty string)
    }
    return value;
  }

  // Handle transaction reference change with validation
  const handleTransactionRefChange = (e) => {
    let value = e.target.value;

    // Prevent spaces in empty field
    value = preventSpacesInEmptyField(value, transactionRef);

    setTransactionRef(value);

    // Validate in real-time but don't show error until user tries to submit
    const error = validateField(value, 'transactionRef');
    setFieldErrors(prev => ({
      ...prev,
      transactionRef: error
    }));
  }

  // Handle remarks change with validation
  const handleRemarksChange = (e) => {
    let value = e.target.value;

    // Prevent spaces in empty field
    value = preventSpacesInEmptyField(value, remarks);

    setRemarks(value);

    // Validate in real-time but don't show error until user tries to submit
    const error = validateField(value, 'remarks');
    setFieldErrors(prev => ({
      ...prev,
      remarks: error
    }));
  }

  // Handle key down to prevent space when field is empty
  const handleKeyDown = (e, fieldName, currentValue) => {
    // If field is empty and user presses space, prevent it
    if (!currentValue && e.key === ' ') {
      e.preventDefault();
    }
  }

  // Handle paste to prevent pasting only spaces
  const handlePaste = (e, fieldName) => {
    const pastedText = e.clipboardData.getData('text');

    // If field is empty and pasted text is only spaces, prevent it
    if (!transactionRef && !remarks && pastedText.trim() === '') {
      e.preventDefault();
    }
  }

  const handleSaveAndPay = async () => {
    // Validate both fields
    const transactionRefError = validateField(transactionRef, 'transactionRef');
    const remarksError = validateField(remarks, 'remarks');

    setFieldErrors({
      transactionRef: transactionRefError,
      remarks: remarksError
    });

    // If there are any validation errors, stop submission
    if (transactionRefError || remarksError) {
      if (transactionRefError) {
        alert(transactionRefError); // This now contains the translated message
      }
      return;
    }

    // Trim values before submitting to API
    const trimmedTransactionRef = transactionRef.trim();
    const trimmedRemarks = remarks.trim();

    setSaving(true)
    try {
      const tokenString = sessionStorage.getItem('usertoken') || ''
      const v2 = tokenString.replace(/["']+/g, '')

      const payload = {
        bill_id: billId,
        hospital_id: hospitalId,
        remarks: trimmedRemarks, // Use trimmed value
        payment_mode: {
          transaction_type: 'bank',
          transaction_reference: trimmedTransactionRef, // Use trimmed value
        },
        paid_amount: invoiceData?.liability || invoiceData?.balance_amount || 0,
      }

      const url = `${APIURL}/api/v1/staff/vendor-payment/`
      const response = await axios.post(url, payload, {
        headers: { Authorization: 'Token ' + v2 },
      })

      // Check if response indicates success
      if (response.data && response.data.status === 'success') {
        alert(t('alerts.paymentRecorded'))
        if (onPaymentSuccess) {
          onPaymentSuccess()
        } else {
          onHide()
        }
      } else {
        if (response.data.status === 'error' && response.data.message &&
          response.data.message === 'Identification number is not updated, so cannot proceed with the payment ') {
          alert(t('alerts.identificationNotUpdated'));
        } else {
          alert(t('alerts.failedToSave'));
        }
      }

    } catch (err) {
      console.error('Payment save error:', err)

      // Handle specific error response from API
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.status === 'error' && errorData.message &&
          errorData.message === 'Identification number is not updated, so cannot proceed with the payment ') {
          alert(t('alerts.identificationNotUpdated'));
        } else {
          alert(t('alerts.failedToSave'));
        }
      } else {
        // Network or other errors
        alert(t('alerts.failedToSave'));
      }
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2) 
    return `${day}-${month}-${year}`
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="fc-invoice-modal">
      <Modal.Header closeButton className="fc-invoice-modal-header fc-no-print">
        <Modal.Title>{t('title', 'Invoice')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="fc-invoice-modal-body" ref={printRef}>
        {loading ? (
          <div className="fc-invoice-loading">
            <div className="fc-invoice-spinner"></div>
            <p>{t('loading.loadingInvoice')}</p>
          </div>
        ) : error ? (
          <div className="fc-invoice-error">
            <i className="fa fa-exclamation-circle"></i>
            <p>{error}</p>
          </div>
        ) : invoiceData ? (
          <div className="fc-invoice-content">
            {/* Header */}
            <div className="fc-invoice-header">
              <div className="fc-invoice-logo">
                <img src={logo} alt="Aryal Care" />
              </div>
              <div className="fc-invoice-title-section">
                <h2 className="fc-invoice-title">{t('invoiceTitle')}</h2>
                <div className="fc-invoice-date">
                  <span className="fc-date-label">{t('labels.date')}</span>
                  <span className="fc-date-value">{formatDate(new Date())}</span>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="fc-invoice-details-grid">
              <div className="fc-details-left">
                <table className="fc-details-table">
                  <tbody>
                    <tr>
                      <td className="fc-detail-label">{t('labels.itemService')}</td>
                      <td className="fc-detail-value">
                        {invoiceData.item_name || t('defaults.license')}
                      </td>
                    </tr>
                    <tr>
                      <td className="fc-detail-label">{t('labels.issuedDate')}</td>
                      <td className="fc-detail-value">{formatDate(invoiceData.bill_generated_date)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="fc-details-right">
                <div className="fc-amount-box fc-total">
                  <div className="fc-amount-label">{t('labels.total')}</div>
                  <div className="fc-amount-value">
                    {formatAmount(invoiceData.total_license_amount, invoiceData.currency)}
                  </div>
                </div>
                <div className="fc-amount-box fc-payable">
                  <div className="fc-amount-label">{t('labels.payable')}</div>
                  <div className="fc-amount-value fc-payable-amount">
                    {formatAmount(invoiceData.liability || invoiceData.balance_amount, invoiceData.currency)}
                  </div>
                </div>
              </div>
            </div>

            <div className="fc-tax-note">
              <i className="fa fa-info-circle"></i>
              <span>{t('notes.priceIncludesTax')}</span>
            </div>

            <div className="fc-payment-inputs">
              <div className="fc-input-group">
                <label htmlFor="fc-transactionRef">{t('labels.transactionRef')}</label>
                <input
                  type="text"
                  id="fc-transactionRef"
                  value={transactionRef}
                  onChange={handleTransactionRefChange}
                  onKeyDown={(e) => handleKeyDown(e, 'transactionRef', transactionRef)}
                  onPaste={(e) => handlePaste(e, 'transactionRef')}
                  placeholder={t('placeholders.transactionRef')}
                  className={`fc-form-input ${fieldErrors.transactionRef ? 'fc-input-error' : ''}`}
                />
                {fieldErrors.transactionRef && (
                  <div className="fc-field-error">{fieldErrors.transactionRef}</div>
                )}
              </div>

              <div className="fc-input-group">
                <label htmlFor="fc-remarks">{t('labels.remarks')}</label>
                <textarea
                  id="fc-remarks"
                  value={remarks}
                  onChange={handleRemarksChange}
                  onKeyDown={(e) => handleKeyDown(e, 'remarks', remarks)}
                  onPaste={(e) => handlePaste(e, 'remarks')}
                  placeholder={t('placeholders.remarks')}
                  className={`fc-form-textarea ${fieldErrors.remarks ? 'fc-input-error' : ''}`}
                  rows="1"
                />
                {fieldErrors.remarks && (
                  <div className="fc-field-error">{fieldErrors.remarks}</div>
                )}
              </div>
            </div>

            {/* Note */}
            <div className="fc-invoice-note fc-no-print">
              {t('notes.printWarning')}
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="fc-invoice-modal-footer fc-no-print">
        <button className="fc-action-btn fc-print-btn" onClick={handlePrint} disabled={!invoiceData}>
          <i className="fa fa-print"></i>
          {t('buttons.print', 'Print')}
        </button>
        <button
          className="fc-action-btn fc-save-btn"
          onClick={handleSaveAndPay}
          disabled={saving || !invoiceData}
        >
          {saving ? (
            <>
              <span className="fc-spinner-small"></span>
              {t('buttons.saving')}
            </>
          ) : (
            <>
              <i className="fa fa-save"></i>
              {t('buttons.saveAndPay')}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  )
}